import React, { useState } from "react";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import { passphraseProblem } from "./e2ee.js";

// Lives on the Account & sync screen: turn end-to-end encryption on or off, or change the passphrase.
export default function EncryptionCard({ on, locked, hasBusiness = false, onEnable, onDisable, onUnlock, onForgot }) {
  const [mode, setMode] = useState(null);       // null | "enable" | "change" | "disable"
  const [pass, setPass] = useState("");
  const [again, setAgain] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const close = () => { setMode(null); setPass(""); setAgain(""); setUnderstood(false); setErr(""); setBusy(false); };

  async function submit() {
    setErr("");
    if (mode === "enable" || mode === "change") {
      const problem = passphraseProblem(pass);
      if (problem) { setErr(problem); return; }
      if (pass !== again) { setErr("The two passphrases don't match."); return; }
      if (!understood) { setErr("Please tick the box to confirm you understand."); return; }
    }
    setBusy(true);
    try {
      if (mode === "disable") await onDisable();
      else await onEnable(pass, remember);
      close();
    } catch (e) { setErr(e.message || "Something went wrong."); setBusy(false); }
  }

  return (
    <div className="bg-card rounded-2xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={16} className="text-brass" />
        <span className="text-sm font-semibold">End-to-end encryption</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full ml-auto" style={{ background: "var(--card2)", color: "var(--ivory-dim)" }}>{on ? (locked ? "On, locked" : "On") : "Off"}</span>
      </div>
      <p className="text-xs text-ivory-dim leading-relaxed">
        {on
          ? "Your online copy is encrypted on this device before it's uploaded, with a passphrase only you know. We can't read it."
          : `Encrypt your online copy with a passphrase only you know, so even we can't read it.${hasBusiness ? " Invoices you share by link and any payment setup stay readable by design, since the people receiving them need to see them." : ""}`}
      </p>

      {!mode && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!on && <button onClick={() => setMode("enable")} className="flex-1 bg-gold-grad text-ink font-semibold rounded-full py-2 text-sm">Turn on encryption</button>}
          {on && locked && <button onClick={onUnlock} className="flex-1 bg-gold-grad text-ink font-semibold rounded-full py-2 text-sm">Unlock on this device</button>}
          {on && locked && <button onClick={onForgot} className="flex-1 bg-card2 rounded-full py-2 text-sm font-medium">Forgot passphrase?</button>}
          {on && !locked && <button onClick={() => setMode("change")} className="flex-1 bg-card2 rounded-full py-2 text-sm font-medium">Change passphrase</button>}
          {on && !locked && <button onClick={() => setMode("disable")} className="flex-1 bg-card2 rounded-full py-2 text-sm font-medium" style={{ color: "var(--rust)" }}>Turn off</button>}
        </div>
      )}

      {mode && (
        <div className="mt-4">
          {mode === "disable" ? (
            <p className="text-xs text-ivory-dim leading-relaxed mb-3">Your online copy will be stored in readable form again, and your other devices won't need the passphrase.</p>
          ) : (
            <>
              <input value={pass} onChange={(e) => setPass(e.target.value)} type="password" autoComplete="new-password" placeholder="Encryption passphrase" className="w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
              <input value={again} onChange={(e) => setAgain(e.target.value)} type="password" autoComplete="new-password" placeholder="Type it again" className="w-full rounded-lg px-3 py-2.5 text-sm" />
              <div className="text-[11px] text-ivory-dim mt-2 leading-relaxed">At least 10 characters. Four random words works well. This is separate from your account password.</div>
              <label className="flex items-start gap-2 text-xs mt-3 leading-relaxed">
                <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} className="mt-0.5" />
                <span>I understand that if I forget this passphrase, nobody, including the app's owner, can recover my online data. Back-ups I've saved to a file still work.</span>
              </label>
              <label className="flex items-center gap-2 text-xs text-ivory-dim mt-2"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Remember on this device</label>
              <div className="text-[11px] text-ivory-dim mt-3 leading-relaxed">Older copies saved before you turned this on may stay in the server's own back-ups for a while.</div>
            </>
          )}
          {err && <div className="text-xs mt-3" style={{ color: "var(--rust)" }}>{err}</div>}
          <div className="flex gap-2 mt-4">
            <button onClick={close} className="flex-1 bg-card2 rounded-full py-2 text-sm font-medium">Cancel</button>
            <button onClick={submit} disabled={busy} className="flex-1 flex items-center justify-center gap-1.5 bg-gold-grad text-ink font-semibold rounded-full py-2 text-sm disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}{mode === "disable" ? "Turn off" : mode === "change" ? "Change" : "Encrypt"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
